import { NumberInput, Select, type SelectOption } from "@/deps/ui-library";
import type { BankAccount, ReceiptMethod } from "@/api/types";
import { formatAmount } from "@/lib/money";
import { RECEIPT_METHODS } from "@/lib/receiptMethods";

/** Grouping is off because the page reads the string back: RDS formats on blur. */
const MONEY = { useGrouping: false, minimumFractionDigits: 2, maximumFractionDigits: 2 };

const paisa = (value: string) => Math.round(Number(value || "0") * 100);

function chosen(details: { value: SelectOption | SelectOption[] | null }): string {
  const option = Array.isArray(details.value) ? details.value[0] : details.value;
  return option ? String(option.value) : "";
}

/**
 * What was taken for a cash sale. It's kept with the draft, and whoever issues the invoice posts it
 * as a receipt (ADR-0052). Anything over the total is change, which goes back to the customer and
 * never reaches the ledger. Anything under it stays outstanding on the invoice.
 */
export function CashReceive({
  total,
  received,
  onReceived,
  method,
  onMethod,
  accounts,
  accountId,
  onAccount,
}: {
  total: string;
  received: string;
  onReceived: (value: string) => void;
  method: ReceiptMethod;
  onMethod: (value: ReceiptMethod) => void;
  accounts: BankAccount[];
  accountId: string;
  onAccount: (value: string) => void;
}) {
  const difference = paisa(received) - paisa(total);

  return (
    <div className="flex flex-col gap-3">
      <NumberInput
        label="Amount received"
        required
        value={received}
        onValueChange={(details) => {
          // RDS also reports the value it was handed, and only a figure that differs is typing.
          if (paisa(details.value) !== paisa(received)) onReceived(details.value);
        }}
        formatOptions={MONEY}
        config={{ hideTriggers: true }}
      />
      <Select
        label="Method"
        required
        options={RECEIPT_METHODS.map((option) => ({ value: option, label: option }))}
        value={method}
        onChange={(details) => {
          const picked = chosen(details);
          if (picked) onMethod(picked as ReceiptMethod);
        }}
        showClearButton={false}
      />
      <Select
        label="Into"
        required
        options={accounts.map((option) => ({ value: option.id, label: option.name }))}
        value={accountId}
        onChange={(details) => onAccount(chosen(details))}
        showClearButton={false}
      />
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-fg-muted">Change</span>
        <span className="num text-fg">
          {formatAmount((Math.max(difference, 0) / 100).toFixed(2))}
        </span>
      </div>
      {difference < 0 ? (
        <p className="text-warn-text text-xs">
          {formatAmount((-difference / 100).toFixed(2))} stays outstanding on this invoice.
        </p>
      ) : null}
    </div>
  );
}
